import React, { useState } from 'react';
import logger from '../../utils/logger';
import { PaymentSettings, PaymentMethod, BillingRecord } from '../../types';
import { formatCurrency } from '../../utils/currencyFormatter';
import { Button } from '../ui';
import {
  CreditCardIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

interface PaymentSettingsFormProps {
  settings: PaymentSettings;
  onUpdate: (settings: Partial<PaymentSettings>) => Promise<void>;
  isLoading: boolean;
}

const PaymentSettingsForm: React.FC<PaymentSettingsFormProps> = ({
  settings,
  onUpdate,
  isLoading,
}) => {
  const [activeSection, setActiveSection] = useState<'overview' | 'methods' | 'history'>(
    'overview'
  );

  const getSubscriptionStatusColor = (status: PaymentSettings['subscriptionStatus']) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'trialing':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'past_due':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'canceled':
      case 'inactive':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSubscriptionStatusText = (status: PaymentSettings['subscriptionStatus']) => {
    switch (status) {
      case 'active':
        return '有効';
      case 'trialing':
        return 'トライアル中';
      case 'past_due':
        return '支払い遅延';
      case 'canceled':
        return 'キャンセル済み';
      case 'inactive':
        return '無効';
      default:
        return '不明';
    }
  };

  const handleCustomerPortalRedirect = async () => {
    // TODO: Stripe Customer Portalへのリダイレクト処理を実装
    // const portalUrl = await createCustomerPortalSession(settings.stripeCustomerId);
    // window.open(portalUrl, '_blank');
    logger.info('Stripe Customer Portalへのリダイレクト（未実装）');
  };

  const handleAddPaymentMethod = async () => {
    // TODO: 新しい支払い方法追加処理を実装
    logger.info('支払い方法の追加（未実装）');
  };

  return (
    <div className='space-y-6'>
      {/* セクションナビゲーション */}
      <div className='border-b border-gray-200 dark:border-gray-700'>
        <nav className='flex space-x-8'>
          {[
            { id: 'overview', label: 'サブスクリプション概要', icon: BanknotesIcon },
            { id: 'methods', label: '支払い方法', icon: CreditCardIcon },
            { id: 'history', label: '請求履歴', icon: DocumentTextIcon },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeSection === id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className='h-4 w-4' />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* サブスクリプション概要 */}
      {activeSection === 'overview' && (
        <div className='space-y-6'>
          {/* 現在のプラン */}
          <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-6'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>現在のプラン</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  プラン名
                </label>
                <p className='text-lg font-semibold text-gray-900 dark:text-white'>
                  {settings.subscriptionPlan}
                </p>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  ステータス
                </label>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getSubscriptionStatusColor(
                    settings.subscriptionStatus
                  )}`}
                >
                  {settings.subscriptionStatus === 'active' ? (
                    <CheckCircleIcon className='h-4 w-4 mr-2' />
                  ) : (
                    <ExclamationTriangleIcon className='h-4 w-4 mr-2' />
                  )}
                  {getSubscriptionStatusText(settings.subscriptionStatus)}
                </span>
              </div>
            </div>
          </div>

          {/* 請求先メール */}
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              請求先メールアドレス
            </label>
            <input
              type='email'
              value={settings.billingEmail}
              onChange={e => onUpdate({ billingEmail: e.target.value })}
              className='block w-full max-w-md rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
              disabled={isLoading}
            />
            <p className='mt-1 text-sm text-gray-500'>請求書とサブスクリプション通知の送信先</p>
          </div>

          {/* Customer Portalボタン */}
          <div className='pt-4'>
            <Button
              variant='primary'
              onClick={handleCustomerPortalRedirect}
              disabled={isLoading || !settings.stripeCustomerId}
              className='flex items-center space-x-2'
            >
              <CreditCardIcon className='h-4 w-4' />
              <span>支払い情報を管理</span>
            </Button>
            <p className='mt-2 text-sm text-gray-500'>
              Stripe Customer Portalで支払い方法の変更、請求書の確認が可能です
            </p>
          </div>
        </div>
      )}

      {/* 支払い方法 */}
      {activeSection === 'methods' && (
        <div className='space-y-6'>
          <div className='flex justify-between items-center'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
              登録済み支払い方法
            </h3>
            <Button
              variant='secondary'
              onClick={handleAddPaymentMethod}
              disabled={isLoading}
              className='flex items-center space-x-2'
            >
              <CreditCardIcon className='h-4 w-4' />
              <span>新しい支払い方法を追加</span>
            </Button>
          </div>

          {settings.paymentMethods.length === 0 ? (
            <div className='text-center py-12 text-gray-500'>
              <CreditCardIcon className='h-12 w-12 mx-auto mb-4 text-gray-300' />
              <p>登録済みの支払い方法がありません</p>
            </div>
          ) : (
            <div className='space-y-4'>
              {settings.paymentMethods.map(method => (
                <PaymentMethodCard key={method.id} method={method} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* 請求履歴 */}
      {activeSection === 'history' && (
        <div className='space-y-6'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white'>請求履歴</h3>

          {settings.billingHistory.length === 0 ? (
            <div className='text-center py-12 text-gray-500'>
              <DocumentTextIcon className='h-12 w-12 mx-auto mb-4 text-gray-300' />
              <p>請求履歴がありません</p>
            </div>
          ) : (
            <div className='overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
              <table className='min-w-full divide-y divide-gray-300 dark:divide-gray-600'>
                <thead className='bg-gray-50 dark:bg-gray-700'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      日付
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      説明
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      金額
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      ステータス
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      請求書
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600'>
                  {settings.billingHistory.map(record => (
                    <BillingRecordRow key={record.id} record={record} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// 支払い方法カードコンポーネント
const PaymentMethodCard: React.FC<{ method: PaymentMethod }> = ({ method }) => {
  const getCardIcon = (type: PaymentMethod['type']) => {
    switch (type) {
      case 'card':
        return <CreditCardIcon className='h-6 w-6' />;
      case 'bank_account':
        return <BanknotesIcon className='h-6 w-6' />;
      default:
        return <CreditCardIcon className='h-6 w-6' />;
    }
  };

  const getCardBrand = (brand?: string) => {
    if (!brand) return '';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  return (
    <div className='border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex items-center justify-between'>
      <div className='flex items-center space-x-4'>
        <div className='text-gray-400'>{getCardIcon(method.type)}</div>
        <div>
          <p className='font-medium text-gray-900 dark:text-white'>
            {method.type === 'card' ? getCardBrand(method.brand) : '銀行口座'} ****{method.last4}
          </p>
          {method.type === 'card' && method.expiryMonth && method.expiryYear && (
            <p className='text-sm text-gray-500'>
              有効期限: {method.expiryMonth.toString().padStart(2, '0')}/{method.expiryYear}
            </p>
          )}
          {method.isDefault && (
            <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 mt-1'>
              デフォルト
            </span>
          )}
        </div>
      </div>
      <p className='text-sm text-gray-500'>
        追加日: {new Date(method.createdAt).toLocaleDateString('ja-JP')}
      </p>
    </div>
  );
};

// 請求履歴行コンポーネント
const BillingRecordRow: React.FC<{ record: BillingRecord }> = ({ record }) => {
  const getStatusColor = (status: BillingRecord['status']) => {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusText = (status: BillingRecord['status']) => {
    switch (status) {
      case 'paid':
        return '支払い済み';
      case 'pending':
        return '支払い待ち';
      case 'failed':
        return '支払い失敗';
      default:
        return '不明';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  return (
    <tr>
      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100'>
        {formatDate(record.createdAt)}
      </td>
      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100'>
        {record.description}
      </td>
      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100'>
        {formatCurrency(record.amount, { currency: record.currency })}
      </td>
      <td className='px-6 py-4 whitespace-nowrap'>
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
            record.status
          )}`}
        >
          {getStatusText(record.status)}
        </span>
      </td>
      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
        {record.invoiceUrl ? (
          <a
            href={record.invoiceUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary-600 hover:text-primary-500 flex items-center space-x-1'
          >
            <DocumentTextIcon className='h-4 w-4' />
            <span>表示</span>
          </a>
        ) : (
          '-'
        )}
      </td>
    </tr>
  );
};

export default PaymentSettingsForm;
